import React from "react";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "../../../lib/date";
import { formatLastActive } from "../../../utils/dateFormatUtils";
import type { DataTableCellConfig } from "./types";

function getRecordValue<T>(record: T, key: keyof T): unknown {
  return (record as Record<keyof T, unknown>)[key];
}

function renderAgeCell<T>(cell: Extract<DataTableCellConfig<T>, { type: "age" }>, record: T) {
  const age = getRecordValue(record, cell.ageKey);
  const dateOfBirth = getRecordValue(record, cell.dateOfBirthKey);
  const hasAgeValue = age !== null && age !== undefined;
  const hasDateOfBirthValue = dateOfBirth !== null && dateOfBirth !== undefined;
  const hasAge = typeof age === "number" && age > 0;

  if (!hasAgeValue && !hasDateOfBirthValue) {
    return <span>--</span>;
  }

  return (
    <div className="text-center">
      {hasAge && age}
      {hasAge && hasDateOfBirthValue && <p className="mb-1" />}
      {hasDateOfBirthValue && dayjs(dateOfBirth as string | Date).format(DEFAULT_DATE_FORMAT)}
    </div>
  );
}

function renderLastActiveCell<T>(
  cell: Extract<DataTableCellConfig<T>, { type: "last_active" }>,
  record: T,
) {
  const value = getRecordValue(record, cell.valueKey);
  const { date, timeAgo } = formatLastActive(typeof value === "string" ? value : null);

  if (!date) {
    return <span className="text-gray-400 dark:text-gray-500">-</span>;
  }

  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{date}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{timeAgo}</span>
    </div>
  );
}

export function renderDataTableCell<T>(cell: DataTableCellConfig<T>, record: T): React.ReactNode {
  switch (cell.type) {
    case "age":
      return renderAgeCell(cell, record);
    case "last_active":
      return renderLastActiveCell(cell, record);
  }
}
